import { brandName } from "../brands";

const LOGO_URL = "/ito-logo.png";

// Resolve the company name to print: explicit brand on the doc wins,
// else fall back to company settings legal_name/name.
function resolveBrandName(doc, company) {
  const fromBrand = doc && doc.company_brand ? brandName(doc.company_brand) : "";
  return fromBrand || company?.legal_name || company?.name || "ITO ENGINEERING INFRA PVT LTD";
}

function fmtINR(n) {
  const v = Number(n || 0);
  return "Rs. " + v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

// Print any ISO date as DD/MM/YYYY for PDFs.
function fmtDate(value) {
  if (!value) return "";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const p = (x) => String(x).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  return s;
}

// jsPDF core fonts (WinAnsi) can't render many Unicode glyphs (arrows, smart quotes, dashes)
// and show garbage like "!'". Replace them with safe ASCII equivalents.
function san(s) {
  if (s == null) return "";
  return String(s)
    .replace(/[\u2192\u2794\u279C\u2799]/g, "->")
    .replace(/[\u2190]/g, "<-")
    .replace(/[\u2018\u2019\u201B]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[\u2026]/g, "...")
    .replace(/[\u00A0]/g, " ")
    .replace(/[\u2022]/g, "*");
}

async function loadLogoDataUrl() {
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

function drawITOHeader(doc, company, logoData, copyLabel, brandNameStr, subTitle) {
  // logo
  if (logoData) {
    try { doc.addImage(logoData, "PNG", 12, 8, 28, 28); } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("PDF logo render skipped:", e?.message || e); }
  }

  // Copy label — centered at very top (own line, no overlap)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(200, 30, 30);
  doc.text(copyLabel, 105, 10, { align: "center" });

  // company name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(200, 30, 30);
  doc.text((brandNameStr || company?.legal_name || company?.name || "ITO ENGINEERING INFRA PVT LTD").toUpperCase(), 105, 17, { align: "center" });

  doc.setFontSize(7.5);
  doc.setTextColor(60);
  doc.setFont("helvetica", "normal");
  if (company?.address) doc.text(`HEAD OFFICE: ${company.address}`, 105, 22, { align: "center" });
  if (company?.phone) doc.text(`TEL.: ${company.phone}`, 105, 26, { align: "center" });
  if (company?.email) doc.text(`E-mail: ${company.email}`, 105, 30, { align: "center" });

  const line = [
    company?.gst_number && `GST NO.: ${company.gst_number}`,
    company?.msme_number && `MSME: ${company.msme_number}`,
    company?.pan_number && `PAN: ${company.pan_number}`,
  ].filter(Boolean).join("    ");
  if (line) doc.text(line, 105, 34, { align: "center" });

  // tagline — centered single line
  doc.setFontSize(6.5);
  doc.setTextColor(120);
  doc.text("ALL BOOKING FROM MUMBAI HEAD OFFICE   •   SERVICE FOR ALL OVER INDIA", 105, 37.5, { align: "center" });

  doc.setDrawColor(200, 30, 30);
  doc.setLineWidth(0.6);
  doc.line(10, 40, 200, 40);

  doc.setTextColor(0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(subTitle || "TRANSPORT DIVISION  |  CONSIGNMENT NOTE", 105, 45, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(80);
  if (company?.registration_no) {
    doc.text(
      `Certificate of Registration under Carriage by Road Act 2007  |  Reg. No. ${company.registration_no}  |  Issue: ${company.registration_issue || "-"}  Valid: ${company.registration_valid || "-"}`,
      105, 49, { align: "center" }
    );
  }
  doc.setTextColor(0);
  return 54;
}


const TABLE_MARGIN = { left: 10, right: 10 };
const BASE_STYLES = {
  fontSize: 9,
  cellPadding: { top: 2.2, right: 2.5, bottom: 2.2, left: 2.5 },
  valign: "middle",
  lineColor: [205, 205, 205],
  lineWidth: 0.2,
  textColor: 25,
  overflow: "linebreak",
};
const HEAD_STYLES = { fillColor: [236, 236, 236], textColor: 30, fontStyle: "bold", halign: "center", valign: "middle", lineColor: [205, 205, 205], lineWidth: 0.2 };
const RED = [196, 30, 40];
const HEAD_RED = { fillColor: RED, textColor: 255, fontStyle: "bold", halign: "center", valign: "middle", lineColor: RED, lineWidth: 0.2 };

// normalise any stored value ("LR No. 25439", "LR-20260160", "25439") to its core number
function cleanNo(v) {
  return String(v || "").replace(/^\s*(lr\s*no\.?|lr[-\s]*|no\.?)\s*/i, "").trim() || "-";
}
// full display label, always "LR No. <number>"
function lrLabel(v) {
  const n = cleanNo(v);
  return n && n !== "-" ? `LR No. ${n}` : "LR No.";
}

// LR / Consignment Note header — LANDSCAPE (A4 297x210). Logo + company (left),
// one combined registration/service box (right), centered title + prominent No./Date.
function drawLRHeader(doc, company, logoData, copyLabel, brandNameStr, lr) {
  const name = (brandNameStr || company?.legal_name || company?.name || "ITO ENGINEERING INFRA PVT LTD").toUpperCase();
  if (logoData) {
    try { doc.addImage(logoData, "PNG", 10, 9, 20, 20); } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("PDF logo skipped:", e?.message || e); }
  }
  // LEFT — company identity (kept clear of the right box at x=212)
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...RED);
  doc.text(name, 33, 15, { maxWidth: 172 });
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(70);
  let ly = 20;
  if (company?.address) { const al = doc.splitTextToSize(san(`Head Office: ${company.address}`), 175); doc.text(al, 33, ly); ly += 3.4 * al.length; }
  if (company?.phone) { doc.text(san(`Tel: ${company.phone}`), 33, ly); ly += 3.4; }
  if (company?.email) { doc.text(san(`Email: ${company.email}`), 33, ly); ly += 3.4; }
  const idbits = [company?.gst_number && `GSTIN: ${company.gst_number}`, company?.pan_number && `PAN: ${company.pan_number}`, company?.msme_number && `MSME: ${company.msme_number}`].filter(Boolean);
  if (idbits.length) { doc.setFont("helvetica", "bold"); doc.setTextColor(40); doc.text(san(idbits.join("    ")), 33, ly, { maxWidth: 175 }); }

  // RIGHT — ONE combined box
  doc.setDrawColor(...RED); doc.setLineWidth(0.4); doc.rect(212, 9, 77, 22);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.6); doc.setTextColor(...RED);
  doc.text("ALL BOOKINGS AT MUMBAI HEAD OFFICE", 250.5, 14.5, { align: "center" });
  doc.text("SERVICE ALL OVER INDIA", 250.5, 17.8, { align: "center" });
  doc.setDrawColor(215); doc.setLineWidth(0.2); doc.line(214, 20.3, 287, 20.3);
  doc.setFont("helvetica", "normal"); doc.setFontSize(5.6); doc.setTextColor(95);
  doc.text("Registered under the Carriage by Road Act, 2007", 250.5, 23.8, { align: "center" });
  if (company?.registration_no) {
    doc.text(`Reg. No. ${company.registration_no}    Issue: ${company.registration_issue || "-"}    Valid: ${company.registration_valid || "-"}`, 250.5, 27.4, { align: "center", maxWidth: 73 });
  }

  // Divider
  doc.setDrawColor(...RED); doc.setLineWidth(0.6); doc.line(8, 33, 289, 33);

  // Copy label + centered title
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...RED);
  doc.text(copyLabel, 148.5, 38, { align: "center" });
  doc.setFontSize(14); doc.setTextColor(0);
  doc.text("CONSIGNMENT NOTE", 148.5, 44.5, { align: "center" });
  doc.setFontSize(7); doc.setTextColor(95); doc.setFont("helvetica", "normal");
  doc.text("Transport Division  ·  Received in apparent good order, subject to the conditions of carriage overleaf.", 148.5, 48.5, { align: "center" });

  // Centered, prominent No. & Date
  doc.setDrawColor(...RED); doc.setLineWidth(0.4); doc.rect(106, 51, 85, 9.5);
  doc.setDrawColor(220); doc.setLineWidth(0.2); doc.line(148.5, 51, 148.5, 60.5);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11.5); doc.setTextColor(...RED);
  doc.text(lrLabel(lr?.lr_number), 127, 57.4, { align: "center" });
  doc.setTextColor(0);
  doc.text(`Date  ${fmtDate(lr?.lr_date) || "-"}`, 170, 57.4, { align: "center" });
  return 64;
}

export { LOGO_URL, resolveBrandName, fmtINR, fmtDate, san, loadLogoDataUrl, drawITOHeader, drawLRHeader, cleanNo, lrLabel, TABLE_MARGIN, BASE_STYLES, HEAD_STYLES, HEAD_RED, RED };
